/**
 * 작업 화면 배치 — 새로고침해도 **보던 화면 그대로** 이어지게 (2026-10-05).
 *
 * 무엇을 복원하고 무엇을 하지 않는지는 한 곳(이 파일)에서 정한다. 화면마다 따로 기억하면 한쪽만 복원돼
 * 「패널은 열렸는데 안의 선택은 비었다」 같은 모순이 생긴다.
 *
 * 복원한다 — 작업 화면의 배치·위치(사용자별 저장, 로그아웃하면 지운다 — userStorage):
 *   활성 에디터 탭 · 오른쪽 패널(열림·종류·쿼리/튜닝챌린지) · 챌린지에서 고른 챌린지·문제(모드별) ·
 *   AI 패널 · 분석 모드 · 에디터/결과 수직분할 · 결과창 닫기 · 에디터/결과 크기 · 탐색기 접기(직접 접은 것만)
 * 복원하지 않는다:
 *   실행 결과·실행계획 탭 — 서버 커서·세션이 새로고침으로 사라진다(다시 실행)
 *   창분리 — 브라우저 팝업이라 사용자 동작 없이 다시 띄우면 막힌다
 *   대화상자 창(연결 관리·모니터링·PL/SQL 편집기 등) — 창은 닫힌 채 시작, 창 안의 작업물은 다시 열면 이어진다
 *   **예외(2026-10-12 사용자 요청)** — 튜닝 사례 · SQL 튜닝 · SQL 진단 리포트 창은 「열려 있었는지」 와 「무엇을 보고 있었는지」
 *   (고른 사례 / 작업·단계)를 복원한다(`windows`). 오래 머무는 작업 창이라 새로고침으로 닫히면 찾아 들어가는 길이 길다.
 *   설계·튜닝 가이드 창(탭·고른 가이드)과 테이블 분석 창(탭)도 같다 — 테이블 분석의 수집 결과는 저장하지 않는 것이라 다시 수집한다.
 *   SQL 빌더도 열림을 복원한다(2026-10-31) — 빌더의 SQL 은 저장하지 않으므로 빈 채로 열린다.
 *   **독립 연결 창(오라클 성능 분석·세션 분석·PL/SQL 편집기 등)도 새로고침 뒤에 그 연결로 다시 연다**(2026-10-31 사용자 결정 — 10-12 에는 대상 DB 조회를 이유로 「다시 열기」 를 물었다).
 *   열려 있던 창과 그 연결을 기억한다(`monitor` — Header 가 연다). 연결이 없어졌고 고를 것이 여럿이면 열지 않는다.
 *   **저장하지 않은 고침은 복원하지 않는다** — 위치만이다(양식에는 SQL·이름이 들어 있어 브라우저에 남길 것을 따로 정해야 한다).
 *
 * 저장 형식 호환(docs/schema-compat.md): 모르는 필드는 버리지 않고, 더 새 판이 쓴 값이면 덮어쓰지 않는다.
 * 사용자 취향(편집기 옵션·확대 등)은 여기가 아니라 appSettings 다 — 이것은 「지금 하던 일의 상태」 다.
 */
import { getUserData, setUserData } from './userStorage';
import { IS_STANDALONE } from '../config/features';

const KEY = 'workspace_layout';
const VERSION = 1;
// 'tuning'(SQL 실행계획 분석)은 없앴다(2026-10-26 — 편집기 실행계획에 합쳤다). 옛 저장본의 'tuning' 은 닫힌 것으로 읽는다
export const SIDE_PANEL_VIEWS = ['history', 'challenge'];
export const CHALLENGE_MODES = ['query', 'tuning'];
/** 열림·위치를 복원하는 창 — 창마다 { open, id(고른 사례·작업), step(단계) } */
// 쿼리챌린지·튜닝챌린지는 각자의 작업 탭이다(2026-11-04 — 예전에는 SQL 편집 탭 안에서 편집기 자리를 대신했고 한쪽이 다른 쪽을 바꿨다)
export const RESTORED_WINDOWS = ['cases', 'sqlTuning', 'sqlReview', 'sqlAuthor', 'sqlQuality', 'schemaCompare', 'sqlCollect', 'guides', 'tableAnalysis', 'sqlBuilder', 'challengeQuery', 'challengeTuning'];
const ID_RE = /^[A-Za-z0-9_:]{1,64}$/;   // 범용 가이드 id(builtin:tuning:oracle)에 ':' 가 있다
const STEP_RE = /^[a-z]{1,20}$/;
function windowsOf(v) {
  const src = v && typeof v === 'object' ? v : {};
  const out = {};
  for (const k of RESTORED_WINDOWS) {
    const w = src[k] && typeof src[k] === 'object' ? src[k] : {};
    out[k] = {
      open: !!w.open,
      id: typeof w.id === 'string' && ID_RE.test(w.id) ? w.id : null,
      step: typeof w.step === 'string' && STEP_RE.test(w.step) ? w.step : null,
    };
  }
  return out;
}
/** 열려 있던 독립 연결 창과 그 연결 — 새로고침 뒤 그 연결로 다시 연다. 저장 키 `monitor` 는 옛 이름 그대로다(저장 형식). 모르는 모양은 버린다 */
function monitorOf(v) {
  const src = v && typeof v === 'object' ? v : {};
  const open = (Array.isArray(src.open) ? src.open : []).filter(k => typeof k === 'string' && /^[a-z-]{1,30}$/.test(k)).slice(0, 12);
  const conn = {};
  if (src.conn && typeof src.conn === 'object') {
    for (const [k, id] of Object.entries(src.conn)) if (/^[a-z-]{1,30}$/.test(k) && typeof id === 'string' && id.length <= 200) conn[k] = id;
  }
  return { open: [...new Set(open)], conn };
}

function readRaw() {
  try {
    const v = getUserData(KEY, null);
    return v && typeof v === 'object' ? v : {};
  } catch (_e) { return {}; }
}

/**
 * 저장된 배치 — **모순이 없게 정리해서** 돌려준다. 값이 이상하면 기본값(닫힘·없음)이다.
 *  · 분석 모드와 오른쪽 패널은 같은 자리를 쓴다 — 둘 다 켜져 있으면 패널을 남긴다(App.toggleAnalysisMode 와 같은 규칙)
 */
export function loadLayout() {
  const v = readRaw();
  let sidePanelView = SIDE_PANEL_VIEWS.includes(v.sidePanelView) ? v.sidePanelView : null;
  let sidePanelOpen = !!(v.sidePanelOpen && sidePanelView);
  const windows = windowsOf(v.windows);
  // 옛 배치 — 챌린지가 오른쪽 패널(편집기 자리)에 열려 있었다. 이제는 그 종류의 작업 탭으로 연다(2026-11-04)
  if (sidePanelView === 'challenge') {
    if (sidePanelOpen) windows[v.challengeMode === 'tuning' ? 'challengeTuning' : 'challengeQuery'].open = true;
    sidePanelView = 'history';
    sidePanelOpen = false;
  }
  const challenge = v.challenge && typeof v.challenge === 'object' ? v.challenge : {};
  return {
    activeFileId: typeof v.activeFileId === 'string' ? v.activeFileId : null,
    sidePanelOpen,
    sidePanelView,
    challengeMode: CHALLENGE_MODES.includes(v.challengeMode) ? v.challengeMode : 'query',
    challenge,
    // AI 에이전트 — 웹은 기본 펼침(2026-10-19 사용자 요구, 접을 수 있다). 그 전에 저장된 assistantOpen 은 「기본 닫힘」 시절의 값이라
    // 보지 않고, 이 판부터 사용자가 정한 assistantPanel 만 따른다. 스탠드얼론은 AI 없는 판이라 기본 닫힘
    assistantOpen: v.assistantPanel === 'open' ? true : v.assistantPanel === 'closed' ? false : !IS_STANDALONE,
    // 데이터 분석은 작업 탭이다(2026-10-28) — 오른쪽 패널과 자리를 다투지 않는다
    analysisMode: !!v.analysisMode,
    editorSplit: !!v.editorSplit,
    resultSplit: !!v.resultSplit,
    collapseMode: v.collapseMode === 'bottom' ? 'bottom' : 'none',
    // 튜닝챌린지의 DBMS 필터(2026-10-05) — '' = 전체
    tuningDbms: ['oracle', 'postgresql'].includes(v.tuningDbms) ? v.tuningDbms : '',
    splitSize: Number.isFinite(v.splitSize) && v.splitSize > 0 ? v.splitSize : null,
    // 탐색기를 **직접** 접었는가(2026-10-10). 패널 때문에 자동으로 접힌 것은 저장하지 않는다 — 패널 상태에서 다시 나온다
    explorerCollapsed: !!v.explorerCollapsed,
    // 작업 창(튜닝 사례·SQL 튜닝·SQL 진단 리포트)의 열림과 보던 위치 — 값이 이상하면 닫힘·없음
    windows,
    monitor: monitorOf(v.monitor),
  };
}

/** 일부만 고쳐 저장한다. 더 새 판이 쓴 배치면 건드리지 않는다(버전을 끌어내리지 않는다) */
export function saveLayout(patch) {
  try {
    const cur = readRaw();
    if ((Number(cur.version) || 0) > VERSION) return;
    setUserData(KEY, { ...cur, ...patch, version: VERSION });
  } catch (_e) { /* 저장 불가 환경에서도 앱은 동작해야 한다 */ }
}

/** 챌린지에서 고른 것 — 쿼리·튜닝 모드마다 따로(같은 챌린지를 두 모드가 다르게 보고 있을 수 있다) */
export function loadChallengePick(mode) {
  const c = loadLayout().challenge[mode] || {};
  return {
    challengeId: typeof c.challengeId === 'string' ? c.challengeId : '',
    quizNo: Number.isInteger(c.quizNo) ? c.quizNo : null,
  };
}

export function saveChallengePick(mode, { challengeId, quizNo }) {
  const all = readRaw().challenge || {};
  saveLayout({ challenge: { ...all, [mode]: { challengeId: challengeId || '', quizNo: Number.isInteger(quizNo) ? quizNo : null } } });
}
